import React, { useEffect, useState } from 'react';
import { getClientDrops } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { ErrorBanner, Spinner } from './ClientDashboardPage';

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ClientDropsPage() {
  const { theme: T } = useClientTheme();
  const [drops, setDrops] = useState([]);
  const [currentDrop, setCurrentDrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('current');

  useEffect(() => {
    let active = true;
    getClientDrops()
      .then((data) => {
        if (!active) return;
        setDrops(Array.isArray(data?.drops) ? data.drops : []);
        setCurrentDrop(data?.currentDrop || null);
      })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Failed to load drops.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <Spinner T={T} />;
  if (error) return <ErrorBanner T={T}>{error}</ErrorBanner>;

  const card = { borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 };
  const historyDrops = drops.filter((drop) => drop.id !== currentDrop?.id);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 750, color: T.text }}>Drops</div>
        <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>
          Current drop status and historical drop performance.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 20 }}>
        {[
          ['current', 'Current Drop'],
          ['history', 'Drop History'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: view === key ? `2px solid ${T.gold}` : '2px solid transparent', color: view === key ? T.gold : T.tabInactive, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', marginBottom: -1 }}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'current' && currentDrop ? (
        <section style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Current Drop</div>
              <div style={{ fontSize: 18, color: T.text, fontWeight: 800, marginTop: 5 }}>{currentDrop.name || 'Untitled drop'}</div>
            </div>
            <StatusPill status={currentDrop.status} T={T} />
          </div>
          <DropMetrics drop={currentDrop} T={T} />
          <PiecePerformance drop={currentDrop} T={T} />
        </section>
      ) : null}

      {view === 'current' && !currentDrop ? (
        <section style={card}>
          <div style={{ fontSize: 13, color: T.textMuted }}>No current drop is visible in the portal yet.</div>
        </section>
      ) : null}

      {view === 'history' ? (
      <section style={card}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Drop History</div>
        {historyDrops.length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {historyDrops.map((drop) => (
              <div key={drop.id} style={{ borderRadius: 8, border: `1px solid ${T.border}`, padding: 12, background: T.id === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{drop.name || 'Untitled drop'}</div>
                    <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
                      {formatDate(drop.launchDate)} to {formatDate(drop.reservationEndDate)}
                    </div>
                  </div>
                  <StatusPill status={drop.status} T={T} />
                </div>
                <DropMetrics drop={drop} T={T} compact />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>No previous drops are visible in the portal yet.</div>
        )}
      </section>
      ) : null}
    </div>
  );
}

function DropMetrics({ drop, T, compact = false }) {
  const sales = drop?.sales || {};
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: compact ? 12 : 0 }}>
      <Metric label="Revenue" value={formatMoney(sales.revenueCents || 0)} T={T} compact={compact} />
      <Metric label="Reservations" value={sales.reservations || 0} T={T} compact={compact} />
      <Metric label="Units" value={sales.units || 0} T={T} compact={compact} />
      <Metric label="Products" value={`${drop.productReadyCount || 0}/${drop.productCount || 0}`} T={T} compact={compact} />
    </div>
  );
}

function PiecePerformance({ drop, T }) {
  const pieces = Array.isArray(drop?.pieces) ? drop.pieces : [];
  return (
    <section style={{ marginTop: 18 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Piece Performance</div>
      {pieces.length ? (
        <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: T.text, fontSize: 12 }}>
            <thead>
              <tr>
                {['Piece', 'Status', 'Reservations', 'Units', 'Revenue'].map((heading) => (
                  <th key={heading} style={{ textAlign: 'left', padding: '9px 10px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pieces.map((piece) => (
                <tr key={piece.productId || piece.lookId || piece.productTitle}>
                  <td style={{ padding: '10px', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontWeight: 800 }}>{piece.productTitle || 'Untitled piece'}</div>
                    {(piece.lookId || piece.productId) ? <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{piece.lookId || piece.productId}</div> : null}
                  </td>
                  <td style={{ padding: '10px', borderBottom: `1px solid ${T.border}` }}>{piece.status || '-'}</td>
                  <td style={{ padding: '10px', borderBottom: `1px solid ${T.border}`, fontWeight: 800 }}>{piece.reservations || 0}</td>
                  <td style={{ padding: '10px', borderBottom: `1px solid ${T.border}`, fontWeight: 800 }}>{piece.units || 0}</td>
                  <td style={{ padding: '10px', borderBottom: `1px solid ${T.border}`, fontWeight: 800 }}>{formatMoney(piece.revenueCents || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: T.textMuted }}>No campaign pieces are visible for this drop yet.</div>
      )}
    </section>
  );
}

function Metric({ label, value, T, compact }) {
  return (
    <div style={{ borderRadius: 8, border: `1px solid ${T.border}`, padding: compact ? 10 : 12 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.label, fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: compact ? 16 : 20, color: T.text, fontWeight: 800, marginTop: 5 }}>{value}</div>
    </div>
  );
}

function StatusPill({ status, T }) {
  const normalized = String(status || 'draft').toLowerCase();
  const active = ['active', 'scheduled'].includes(normalized);
  return (
    <span style={{
      borderRadius: 999,
      border: `1px solid ${active ? 'rgba(34,197,94,0.42)' : T.border}`,
      color: active ? '#22c55e' : T.textSub,
      padding: '4px 9px',
      fontSize: 10,
      fontWeight: 850,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      whiteSpace: 'nowrap',
    }}>
      {normalized}
    </span>
  );
}
